import { useEffect, useRef } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { redirectToSignIn } from '@/config/aws'
import AppLayout from '@/components/layout/AppLayout'
import CallbackPage from '@/pages/auth/CallbackPage'
import DashboardPage from '@/pages/dashboard/DashboardPage'
import ForkliftListPage from '@/pages/forklifts/ForkliftListPage'
import ForkliftDetailPage from '@/pages/forklifts/ForkliftDetailPage'
import MetricsPage from '@/pages/analytics/MetricsPage'
import LogsPage from '@/pages/logs/LogsPage'
import LoadingSpinner from '@/components/common/LoadingSpinner'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()
  const autoSignInTriggered = useRef(false)

  useEffect(() => {
    if (isLoading || isAuthenticated || autoSignInTriggered.current) return
    autoSignInTriggered.current = true
    // 未認証時のサインイン誘導（本番は portal 集約 / localhost は単独）は
    // api.ts の 401 ハンドラと共通の redirectToSignIn() に集約している。
    redirectToSignIn()
  }, [isLoading, isAuthenticated])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <LoadingSpinner size="lg" />
        <p className="ml-4 text-gray-500">Redirecting to sign in...</p>
      </div>
    )
  }

  return <>{children}</>
}

function App() {
  return (
    <Routes>
      <Route path="/callback" element={<CallbackPage />} />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="forklifts" element={<ForkliftListPage />} />
        <Route path="forklifts/:forkId" element={<ForkliftDetailPage />} />
        <Route path="analytics/metrics" element={<MetricsPage />} />
        <Route path="logs" element={<LogsPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  )
}

export default App
